/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import {
  NormalizedData,
  NormalizedMessage,
  TSequenceData,
  TSequenceDataForConversations,
} from 'types/prompt-sequence-view';

const ROLES = {
  user: new Set(['user', 'human']),
  assistant: new Set(['assistant', 'bot', 'ai', 'gpt', 'model']),
  system: new Set(['system', 'meta', 'context']),
  tool: new Set([
    'tool',
    'tools',
    'function',
    'tool_results',
    'call_id',
    'tool_function',
  ]),
};

export const getRoleKey = function getRoleKey(value: string) {
  // Normalize the value to lowercase for case-insensitive matching
  const lowerValue = value.toLowerCase();

  for (const [key, values] of Object.entries(ROLES)) {
    if (values.has(lowerValue)) {
      return key;
    }
  }

  return '';
};

export function groupConversationsByTraceId(input: NormalizedData) {
  //create a map to group by trace_id
  const groupedByTraceId: {
    [traceId: string]: {
      span_id: string;
      span_type: string;
      subtype: string;
      inputData: NormalizedMessage[];
      outputData: NormalizedMessage[];
      status: string;
      endTime: string;
      model_attributes?: {
        total_tokens?: number;
        completion_tokens?: number;
        prompt_tokens?: number;
      };
    }[];
  } = {};

  input.sequenceData.forEach((sequence) => {
    const traceId = sequence.trace_id;
    if (!groupedByTraceId[traceId]) {
      groupedByTraceId[traceId] = [];
    }
    const seq = {
      span_id: sequence.span_id,
      span_type: sequence.span_type,
      subtype: sequence.subtype,
      inputData: sequence.inputData,
      outputData: sequence.outputData,
      status: sequence.status,
      endTime: sequence.endTime,
      model_attributes: sequence.model_attributes,
    };
    groupedByTraceId[traceId].push(seq);
  });

  // Convert the grouped data into the expected format
  const result: TSequenceDataForConversations[] = [];
  for (const traceId in groupedByTraceId) {
    result.push({
      trace_id: traceId,
      spans_data: groupedByTraceId[traceId],
    });
  }
  return {
    prompt_id: input.prompt_id,
    sequenceData: result,
  };
}

export function extractData(input: any): NormalizedData[] {
  const results: NormalizedData[] = [];

  const groupedPrompts = input;

  // If grouped_prompts is keyed by trace_ids (like in data1)
  if (typeof groupedPrompts === 'object' && !Array.isArray(groupedPrompts)) {
    for (const key in groupedPrompts) {
      const promptGroup = groupedPrompts[key];

      // Case 1: array of trace objects (data1)
      if (Array.isArray(promptGroup)) {
        const prompt_id = key;
        const sequenceData: TSequenceData[] = [];
        for (const trace of promptGroup) {
          const trace_id = trace.trace_id;
          const span_id = trace.span_id;
          const span_type = trace.span_type || '';
          const subtype = trace.subtype || '';
          const inputs = trace['data.input']?.input || trace.input?.input || [];
          const outputs =
            trace['data.output']?.response || trace.output?.response || [];
          const status = trace?.status;
          const endTime = trace?.end_time;
          const startTime = trace?.start_time;
          const modelAttributes = trace?.model_attributes;

          const inputData = normalizeInput(startTime, inputs);
          const outputData = normalizeInput(startTime, outputs);

          sequenceData.push({
            trace_id,
            span_id,
            span_type,
            subtype,
            inputData,
            outputData: outputData,
            status,
            endTime,
            model_attributes: modelAttributes,
          });
        }

        results.push({
          prompt_id,
          sequenceData,
        });
      }

      // Case 2, 3, 4: object with data.input/data.output directly (data2/3/4)
      else if (typeof promptGroup === 'object') {
        const prompt_id = key;
        const trace_id = 'unknown';
        const span_id = 'unknown';
        const inputs = groupedPrompts['data.input']?.input || [];
        const outputs = groupedPrompts['data.output']?.response || [];
        const status = promptGroup?.status;
        const endTime = promptGroup?.end_time;
        const startTime = promptGroup?.start_time;
        const modelAttributes = promptGroup?.model_attributes;

        const inputData = normalizeInput(startTime, inputs);
        const outputData = normalizeInput(startTime, outputs);

        results.push({
          prompt_id,
          sequenceData: [
            {
              trace_id,
              span_id,
              span_type: '',
              subtype: '',
              inputData,
              outputData: outputData,
              status,
              endTime,
              model_attributes: modelAttributes,
            },
          ],
        });
        break; // Only one result expected in these variations
      }
    }
  }

  return results;
}

export function normalizeInput(
  startTime: string,
  input?: string | string[]
): NormalizedMessage[] {
  if (!input) return [];

  const inputs = Array.isArray(input) ? input : [input];

  return inputs.map((item) => {
    try {
      const parsed = new Function(`return [${item}]`)()[0]; // parse "{'human': 'What is an americano?'}"
      const key = Object.keys(parsed)[0];
      return {
        role: getRoleKey(key),
        content: parsed[key],
        startTime,
      };
    } catch (err) {
      // fallback in case of parsing error
      return {
        role: '',
        content: item,
        startTime,
      };
    }
  });
}
